import React, { useEffect } from 'react';
import { Layout } from '../components/Layout/Layout';
import { ArrowLeft, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ESSProjectImporter } from '../components/Admin/ESSProjectImporter';
import { useESSReferenceStore } from '../store/essReferenceStore';
import { useAuthStore } from '../store/authStore';

export const ESSDataManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { referenceProjects, sectorBenchmarks, loadReferenceProjects, loadSectorBenchmarks } = useESSReferenceStore();

  useEffect(() => {
    loadReferenceProjects();
    loadSectorBenchmarks();
  }, []);

  if (!user || !['ADMIN', 'COORDINATEUR'].includes(user.profile_type || '')) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <p className="text-gray-600">Accès réservé aux administrateurs et coordinateurs.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center space-x-4 mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour au dashboard</span>
          </button>
        </div>

        <div className="flex items-center space-x-3 mb-6">
          <Database className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Données ESS</h1>
            <p className="text-gray-600 mt-1">
              Import et gestion des projets de référence pour le benchmarking
            </p>
          </div>
        </div>

        <ESSProjectImporter />

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Projets de référence dans la base
          </h2>

          {referenceProjects.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">
                Aucun projet de référence dans la base. Utilisez l'outil d'import ci-dessus pour ajouter des projets.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {referenceProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">{project.project_name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {project.category} • {project.territory}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-blue-600">{project.project_id}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Benchmarks sectoriels disponibles
          </h2>

          {sectorBenchmarks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">
                Aucun benchmark calculé. Importez des projets pour générer automatiquement les benchmarks.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sectorBenchmarks.map((benchmark) => (
                <div
                  key={benchmark.id}
                  className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{benchmark.category}</h3>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <span className="font-medium">Projets:</span> {benchmark.project_count}
                    </p>
                    <p>
                      <span className="font-medium">Dernière MAJ:</span>{' '}
                      {new Date(benchmark.last_updated).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
